import { RoomApiError, getRoomToken, roomFetch } from './roomFetch';

/**
 * The three-step file upload, kept out of the autosave path entirely — a file is not a scalar answer.
 *
 *   1. POST /room/files/upload-url  → a short-lived signed PUT URL + the storage path (via roomFetch).
 *   2. PUT the bytes STRAIGHT TO GCS (a different origin, raw body, pinned Content-Type). roomFetch
 *      cannot do this — it only POSTs to our API — so this is a plain XHR. XHR (not fetch) because it
 *      is the only browser API that reports UPLOAD progress, and a client on a phone uploading a
 *      multi-MB scan needs a real bar, not an indeterminate spinner.
 *   3. POST /room/files/confirm     → the server stat()-verifies the object and records the answer.
 *
 * The Content-Type is computed ONCE and used for both the signed-URL request and the PUT header: the
 * signed URL pins the content-type, so the PUT must send exactly the same value or GCS rejects it.
 */

export interface UploadProgress {
  loaded: number;
  total: number;
}

/** The presence-only file view the confirm step returns — matches RoomAnswer.file. */
export interface UploadedFile {
  filename: string;
  sizeBytes: number;
  mime: string;
}

function putToSignedUrl(url: string, file: File, contentType: string, onProgress?: (p: UploadProgress) => void): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', url);
    xhr.setRequestHeader('Content-Type', contentType);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) onProgress({ loaded: e.loaded, total: e.total });
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new RoomApiError(xhr.status, 'UPLOAD_FAILED', 'ההעלאה נכשלה. נסו שוב.'));
    };
    xhr.onerror = () => reject(new RoomApiError(0, 'NETWORK', 'ההעלאה נכשלה. בדקו את החיבור ונסו שוב.'));
    xhr.send(file);
  });
}

/**
 * Run the full upload for one field and return the recorded file. Throws RoomApiError on any step;
 * a 401 anywhere clears the session (roomFetch), and the caller re-renders the gate.
 */
export async function uploadRoomFile(
  fieldId: string,
  file: File,
  onProgress?: (p: UploadProgress) => void,
): Promise<UploadedFile> {
  // Guard early so an expired session doesn't waste a signed-URL round trip.
  if (!getRoomToken()) throw new RoomApiError(401, 'UNAUTHORIZED', 'No room session');

  const contentType = file.type || 'application/octet-stream';

  const { uploadUrl, storagePath } = await roomFetch<{ uploadUrl: string; storagePath: string }>('room/files/upload-url', {
    authenticated: true,
    body: { fieldId, filename: file.name, contentType, sizeBytes: file.size },
  });

  await putToSignedUrl(uploadUrl, file, contentType, onProgress);

  const res = await roomFetch<{ file: UploadedFile }>('room/files/confirm', {
    authenticated: true,
    body: { fieldId, storagePath, filename: file.name },
  });
  return res.file;
}
